import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Button from '../Button';
import Card from '../Card';
import CardBody from '../Card/CardBody';
import Image from '../Image';
import { ContentLabel } from '../KeyComponents';

function Check({
  checked,
  label,
  sublabel,
  badge,
  className,
  position,
  description,
  link,
  inputRef,
  onChange,
  indeterminate,
  ...others
}) {

  return (
    <>
      <label className={classNames('form-check-input', className)}>
        {label || sublabel || badge &&
          <ContentLabel label={label} sublabel={sublabel} badge={badge} className="form-check-label" />
        }
        <input
          {...others}
          type="checkbox"
          id={others.id}
          name={others.name}
          ref={inputRef}
          checked={checked}
          value={others.value}
          onChange={onChange}
        />
        <div className={classNames('checkmark', { 'checkmark-right': position === 'right' }, { 'checkmark-indeterminate': indeterminate })} />
      </label>
      {description && <div className="form-description">{description}</div>}
      {link && <Button variant="link" size="sm" className="form-link">{link}</Button>}
    </>
  );
}

Check.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.element) }),
  ]),
  label: PropTypes.string,
  sublabel: PropTypes.string,
  badge: PropTypes.string,
  className: PropTypes.string,
  position: PropTypes.string,
  description: PropTypes.string,
  link: PropTypes.string,
  indeterminate: PropTypes.bool,
};

Check.defaultProps = {
  checked: false,
  onChange: () => { },
  inputRef: undefined,
  label: '',
  sublabel: '',
  badge: '',
  className: '',
  position: '',
  description: '',
  link: '',
  indeterminate: false,
};

function CheckCard({
  checked,
  label,
  sublabel,
  badge,
  image,
  className,
  description,
  inputRef,
  onChange,
  disabled,
  indeterminate,
  ...others
}) {

  const handleCardClick = (event) => {
    if (disabled || event.target.tagName === 'INPUT') {
      return;
    }
    
    const syntheticEvent = {
      target: {
        name: others.name,
        checked: !checked,
        value: others.value
      },
      type: 'change',
      stopPropagation: () => {},
      preventDefault: () => {}
    };
    
    onChange(syntheticEvent);
  };

  const handleCardKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      const syntheticEvent = {
        target: {
          name: others.name,
          checked: !checked,
          value: others.value
        },
        type: 'change',
        stopPropagation: () => {},
        preventDefault: () => {}
      };
      
      onChange(syntheticEvent);
    }
  };

  return (
    <Card
      className={classNames('form-check-card', className, { disabled })}
      onClick={handleCardClick}
      onKeyPress={handleCardKeyPress}
      role="checkbox"
      tabIndex={0}
      aria-checked={checked}
    >
      <CardBody className="form-check-card-body">
        {image && <Image src={image} alt={label} className="form-check-image" />}
        <div className={classNames('form-check')}>
          <label className="form-check-input" htmlFor="checkbox-card">
            <ContentLabel label={label} sublabel={sublabel} badge={badge} className="form-check-label" />
            <input
              {...others}
              disabled={disabled}
              type="checkbox"
              id={others.id}
              name={others.name}
              ref={inputRef}
              checked={checked}
              value={others.value}
              aria-checked={checked}
              onChange={(event) => {
                event.stopPropagation(); // Prevent event from bubbling to the Card
                onChange(event);
              }}
            />
            <div className={classNames('checkmark checkmark-right', { 'checkmark-indeterminate': indeterminate })} />
          </label>
          {description && <div className="form-description">{description}</div>}
        </div>
      </CardBody>
    </Card>
  );
}

CheckCard.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(PropTypes.element) }),
  ]),
  label: PropTypes.string,
  sublabel: PropTypes.string,
  badge: PropTypes.string,
  image: PropTypes.string,
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  indeterminate: PropTypes.bool,
};

CheckCard.defaultProps = {
  checked: false,
  onChange: () => { },
  inputRef: undefined,
  label: '',
  sublabel: '',
  badge: '',
  image: '',
  className: '',
  description: '',
  disabled: false,
  indeterminate: false,
};

const CheckBox = Check;
CheckBox.Card = CheckCard;

export default CheckBox;
